import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown, ChevronRight } from "lucide-react";
import { UpdateLineRequestSchema, type UpdateLineRequest } from "@opus-dt/shared-types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export interface LineHeaderValues {
  name: string;
  responsible: string;
  description: string;
  /** Spec 081 (FR-019) — obrigatória; sem padrão automático, a escolha é do gestor. */
  productionUnit: string;
}

interface CreationProps {
  mode: "creation";
  values: LineHeaderValues;
  onChange: (patch: Partial<LineHeaderValues>) => void;
  nameSuggestion?: string;
}

interface EditProps {
  mode: "edit";
  values: LineHeaderValues;
  onChange: (patch: Partial<LineHeaderValues>) => void;
  isRunning?: boolean;
}

type Props = CreationProps | EditProps;

export function LineHeaderForm(props: Props) {
  const { mode, values, onChange } = props;
  const initiallyExpanded =
    !values.name.trim() || !values.responsible.trim() || !values.productionUnit.trim();
  const [open, setOpen] = useState(initiallyExpanded);
  const nameRef = useRef<HTMLInputElement>(null);
  const responsibleRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const productionUnitRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onFocusHeader(e: Event) {
      const detail = (e as CustomEvent<{ field?: string }>).detail;
      setOpen(true);
      setTimeout(() => {
        const refMap: Record<string, React.RefObject<HTMLInputElement>> = {
          name: nameRef,
          responsible: responsibleRef,
          description: descriptionRef,
          productionUnit: productionUnitRef,
        };
        refMap[detail?.field ?? "name"]?.current?.focus();
      }, 30);
    }
    window.addEventListener("opus:editor:focus-header", onFocusHeader);
    return () => window.removeEventListener("opus:editor:focus-header", onFocusHeader);
  }, []);

  const form = useForm<UpdateLineRequest>({
    resolver: zodResolver(UpdateLineRequestSchema),
    defaultValues: values,
    mode: "onChange",
  });

  useEffect(() => {
    if (mode === "edit") {
      form.reset(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, values.name, values.responsible, values.description, values.productionUnit]);

  const nameError = mode === "edit" ? form.formState.errors.name?.message : undefined;
  const productionUnitError =
    mode === "edit" ? form.formState.errors.productionUnit?.message : undefined;

  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      className="rounded-md border bg-card"
    >
      <summary className="cursor-pointer list-none px-3 py-2 hover:bg-bg-muted-rd">
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-text-tertiary" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary" />
          )}
          <div className="flex min-w-0 flex-1 items-baseline gap-2">
            <span className="text-sm font-medium text-text-primary">
              {values.name.trim() || (mode === "creation" ? "Nova linha" : "Sem nome")}
            </span>
            {!open && values.responsible.trim() ? (
              <span className="truncate text-xs text-text-tertiary">• {values.responsible}</span>
            ) : null}
          </div>
          <span className="text-[10px] font-semibold uppercase tracking-widest text-text-tertiary">
            Cabeçalho
          </span>
        </div>
      </summary>
      <div className="space-y-3 border-t px-3 py-3">
        <div className="space-y-1.5">
          <Label htmlFor="line-header-name">Nome</Label>
          <Input
            id="line-header-name"
            ref={nameRef}
            placeholder={
              mode === "creation" && props.nameSuggestion
                ? `Sugestão: ${props.nameSuggestion}`
                : undefined
            }
            value={values.name}
            onChange={(e) => onChange({ name: e.target.value })}
            className={cn(nameError && "border-rd-danger-500")}
          />
          {nameError ? <p className="text-xs text-rd-danger-700">{nameError}</p> : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="line-header-production-unit">Unidade de produção</Label>
          <Input
            id="line-header-production-unit"
            ref={productionUnitRef}
            placeholder="kg, un, L…"
            value={values.productionUnit}
            onChange={(e) => onChange({ productionUnit: e.target.value })}
            className={cn(productionUnitError && "border-rd-danger-500")}
          />
          <p className="text-xs text-text-tertiary">
            {/* FR-022/FR-023 — trocar a unidade não reconverte o que já foi lançado. */}A unidade
            em que esta linha declara o que produz. Todo contador das máquinas é convertido para
            ela. Trocá-la depois não reconverte o que já foi lançado.
          </p>
          {productionUnitError ? (
            <p className="text-xs text-rd-danger-700">{productionUnitError}</p>
          ) : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="line-header-responsible">Responsável (opcional)</Label>
          <Input
            id="line-header-responsible"
            ref={responsibleRef}
            value={values.responsible}
            onChange={(e) => onChange({ responsible: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="line-header-description">Descrição (opcional)</Label>
          <Input
            id="line-header-description"
            ref={descriptionRef}
            value={values.description}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </div>
        {mode === "edit" && props.isRunning ? (
          <p className="text-xs text-text-tertiary">
            Linha em produção — alterações são confirmadas ao salvar.
          </p>
        ) : null}
      </div>
    </details>
  );
}
